import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "./components/AdminSidebar";
import AdminTopbar from "./components/AdminTopbar";
import AdminDashboard from "./components/AdminDashboard";
import MemberAudit from "./components/MemberAudit";
import MemberList from "./components/MemberList";
import MemberImport from "./components/MemberImport";
import MemberPointsImport from "./components/MemberPointsImport";
import NewsPublish from "./components/NewsPublish";
import NewsList from "./components/NewsList";
import NewsAudit from "./components/NewsAudit";
import ActivityManager from "./components/ActivityManager";
import LinkedActivityManager from "./components/LinkedActivityManager";
import RegistrationAudit from "./components/RegistrationAudit";
import VoteManager from "./components/VoteManager";
import SystemSettings from "./components/SystemSettings";
import NotificationManager from "./components/NotificationManager";
import DataReports from "./components/DataReports";
import FileManager from "./components/FileManager";
import RenewalManager from "./components/RenewalManager";
import RedeemOrders from "./components/RedeemOrders";
import LandingPageEditor from "./components/LandingPageEditor";
import FeedbackManager from "./components/FeedbackManager";
import { getTenantLegacyId } from "../../api/tenantContext";
import { adminAuth } from "./utils/adminAuth";
import { platformImpersonation } from "../platform/utils/platformImpersonation";
import { AdminDataProvider, useAdminData } from "./AdminDataContext";

type Section =
  | "dashboard"
  | "member-audit"
  | "member-list"
  | "member-import"
  | "member-points-import"
  | "renewal-manager"
  | "redeem-orders"
  | "landing-page"
  | "news-publish"
  | "news-list"
  | "news-audit"
  | "file-manager"
  | "activity-list"
  | "linked-activities"
  | "registration-audit"
  | "vote-manager"
  | "notifications"
  | "feedback"
  | "data-reports"
  | "system-settings";

function AdminLayout() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<Section>("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [regFilter, setRegFilter] = useState<{ id?: string; title?: string }>({});
  const [editingNewsItem, setEditingNewsItem] = useState<import("./utils/dataStore").NewsItem | null>(null);

  const impersonation = platformImpersonation.getCurrent();
  const { memberApplications, loading, refreshing, error, tenantLegacyId, reload, storeVersion } =
    useAdminData();
  const pendingCount = memberApplications.length;

  const handleLogout = () => {
    adminAuth.logout();
    navigate("/admin/login", { replace: true });
  };

  const handleExitImpersonation = () => {
    platformImpersonation.end();
    navigate("/platform");
  };

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return <AdminDashboard onNavigate={(s) => setActiveSection(s as Section)} />;
      case "member-audit":
        return <MemberAudit />;
      case "member-list":
        return <MemberList />;
      case "member-import":
        return <MemberImport />;
      case "member-points-import":
        return <MemberPointsImport />;
      case "renewal-manager":
        return <RenewalManager />;
      case "redeem-orders":
        return <RedeemOrders tenantLegacyId={tenantLegacyId} />;
      case "landing-page":
        return <LandingPageEditor />;
      case "news-publish":
        return (
          <NewsPublish
            tenantLegacyId={tenantLegacyId}
            editItem={editingNewsItem}
            onPublished={async () => {
              setEditingNewsItem(null);
              await reload();
              setActiveSection("news-list");
            }}
          />
        );
      case "news-list":
        return (
          <NewsList
            tenantLegacyId={tenantLegacyId}
            reload={reload}
            storeVersion={storeVersion}
            onPublishNew={() => {
              setEditingNewsItem(null);
              setActiveSection("news-publish");
            }}
            onEdit={(item) => {
              setEditingNewsItem(item);
              setActiveSection("news-publish");
            }}
          />
        );
      case "news-audit":
        return <NewsAudit />;
      case "file-manager":
        return <FileManager />;
      case "activity-list":
        return <ActivityManager />;
      case "linked-activities":
        return <LinkedActivityManager />;
      case "registration-audit":
        return (
          <RegistrationAudit filterActivityId={regFilter.id} filterActivityTitle={regFilter.title} />
        );
      case "vote-manager":
        return <VoteManager />;
      case "notifications":
        return <NotificationManager />;
      case "feedback":
        return <FeedbackManager />;
      case "data-reports":
        return <DataReports />;
      case "system-settings":
        return <SystemSettings />;
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {impersonation && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white px-5 py-2.5 flex items-center gap-3 shadow-md">
          <div
            className={`w-6 h-6 ${impersonation.logoColor} rounded-lg flex items-center justify-center flex-shrink-0`}
          >
            <i className="ri-building-4-line text-white text-xs" />
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <i className="ri-shield-star-line text-white/80 text-sm flex-shrink-0" />
            <span className="text-sm font-medium">平台管理员身份查看中</span>
            <span className="text-white/60">·</span>
            <span className="text-sm font-bold truncate">{impersonation.tenantName}</span>
            <span className="text-white/60 text-xs font-mono hidden sm:inline">{impersonation.tenantCode}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-white/70 hidden md:block">
              您正以平台管理员身份查看此租户后台，数据不可修改
            </span>
            <button
              type="button"
              onClick={handleExitImpersonation}
              className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-xl text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line" />
              退出查看 · 返回平台
            </button>
          </div>
        </div>
      )}

      <div className={`${sidebarOpen ? "block" : "hidden"} lg:block flex-shrink-0 ${impersonation ? "mt-[44px]" : ""}`}>
        <AdminSidebar
          activeSection={activeSection}
          onNavigate={(s) => {
            setActiveSection(s as Section);
            if (s !== "registration-audit") setRegFilter({});
          }}
          pendingCount={pendingCount}
          onLogout={handleLogout}
        />
      </div>

      <div className={`flex-1 flex flex-col min-w-0 overflow-hidden ${impersonation ? "mt-[44px]" : ""}`}>
        <AdminTopbar
          activeSection={activeSection}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onRefreshData={() => void reload({ silent: true })}
          refreshing={refreshing}
        />

        <main className="flex-1 p-6 overflow-y-auto">
          {loading && (
            <div className="mb-4 text-sm text-gray-500">正在从服务器加载租户数据…</div>
          )}
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              无法加载数据：{error}
              {import.meta.env.DEV
                ? "（开发环境请确认后端已启动且 Vite 代理指向正确端口）"
                : ""}
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-5">
            <button
              type="button"
              onClick={() => setActiveSection("dashboard")}
              className="hover:text-red-500 cursor-pointer transition-colors"
            >
              管理后台
            </button>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-600">
              {activeSection === "landing-page" && "落地页管理"}
              {activeSection === "dashboard" && "数据总览"}
              {activeSection === "member-audit" && "会员审核"}
              {activeSection === "member-list" && "会员列表"}
              {activeSection === "member-import" && "会员导入"}
              {activeSection === "renewal-manager" && "续费管理"}
              {activeSection === "redeem-orders" && "兑换订单"}
              {activeSection === "news-publish" && "发布资讯"}
              {activeSection === "news-list" && "资讯列表"}
              {activeSection === "news-audit" && "资讯审核"}
              {activeSection === "file-manager" && "文件管理"}
              {activeSection === "activity-list" && "活动管理"}
              {activeSection === "linked-activities" && "关联活动"}
              {activeSection === "registration-audit" && "报名审核"}
              {activeSection === "vote-manager" && "投票管理"}
              {activeSection === "notifications" && "消息通知"}
              {activeSection === "feedback" && "意见反馈"}
              {activeSection === "data-reports" && "数据报表"}
              {activeSection === "system-settings" && "系统设置"}
            </span>
          </div>

          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const impersonation = platformImpersonation.getCurrent();
  const sessionTid = adminAuth.getLoggedInUser()?.tenantLegacyId?.trim() || "";
  const tenantLegacyId = (impersonation?.tenantId ?? (sessionTid || getTenantLegacyId())).trim();

  useEffect(() => {
    if (!adminAuth.isLoggedIn() && !platformImpersonation.isActive()) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  if (!tenantLegacyId.trim()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center space-y-3">
          <p className="text-sm font-semibold text-gray-800">未识别到租户编号</p>
          <p className="text-xs text-gray-500">请重新登录协会管理后台，并确认填写正确的租户编号。</p>
          <button
            type="button"
            className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm"
            onClick={() => {
              adminAuth.logout();
              navigate("/admin/login", { replace: true });
            }}
          >
            返回登录
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminDataProvider tenantLegacyId={tenantLegacyId}>
      <AdminLayout />
    </AdminDataProvider>
  );
}
